/**
 * The picture on a media card.
 *
 * It fetches `/media/{id}/preview`, which is a bounded derivative - never the
 * original. This product holds 8K stills; a list of forty cards drawing
 * originals would move hundreds of megabytes to render thumbnails.
 *
 * The `<img>` points at the endpoint directly rather than at an object URL
 * built from a fetched blob. That is deliberate: the browser's own image cache
 * and the endpoint's `private, max-age` do the work, there is no `URL.revokeObjectURL`
 * lifecycle to get wrong, and nothing is held in JavaScript memory. The cookie
 * goes with a same-origin image request, so authentication is unchanged.
 *
 * A preview that cannot be produced says so. There is no placeholder standing
 * in for a picture, and no retry loop.
 */

import { useState } from "react";

import { apiUrl } from "@/api/config";
import type { MediaAsset } from "@/api/types";
import { formatDuration } from "@/lib/format";

export function MediaPreview({ asset }: { asset: MediaAsset }) {
  const [failed, setFailed] = useState(false);
  const isVideo = asset.media_type === "video";
  const duration = isVideo ? formatDuration(asset.duration_seconds) : null;

  if (failed) {
    return (
      <div className="mediacard__placeholder">
        <span className="mediacard__glyph" aria-hidden="true">
          {isVideo ? "🎞" : "🖼"}
        </span>
        <span>Önizleme oluşturulamadı</span>
      </div>
    );
  }

  return (
    <div className="mediacard__preview">
      <img
        className="mediacard__image"
        src={apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`)}
        // The user's own filename is the only description available; there is
        // no caption or analysis to draw on. Announcing what kind of file it
        // is keeps the row meaningful when the image itself is not.
        alt={`${asset.original_filename} önizlemesi`}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
      {isVideo && (
        // Not colour alone: a glyph and, where the backend measured one, the
        // duration. A video has to be identifiable as a video in greyscale.
        <span className="mediacard__badge">
          <span aria-hidden="true">▶</span>
          <span>{duration && duration !== "—" ? duration : "Video"}</span>
        </span>
      )}
    </div>
  );
}
